import React, { useState, Fragment, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Collapse, Box, Divider } from '@mui/material';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import DOMPurify from 'dompurify';
import Linkify from 'react-linkify';
import moment from 'moment';
import RescuerSelector from '../../../../../components/RescuerSelector';
import useActiveSite from '../../../../../hooks/useActiveSite';
import { formatTime } from '../../../../../helpers/formatters';
import OverlayLoader from '../../../../../components/OverlayLoader';
import CalendarBox from './CalendarBox';
import MinMaxTimePicker from '../../../../../components/MinMaxTimePicker';
import { donationManageActions } from '../../../../../helpers/donations';
import DonationContext from '../../../../../context/DonationContext/DonationContext';
import useActiveUser from '../../../../../hooks/useActiveUser';
import { getRescuersSelectOptions } from '../../../../../helpers/getters';
import NotesTextEditor from '../../../../../components/NotesTextEditor';
import { FREQUENCY_ONCE, SCHEDULE_STATUS_DELETED } from '../../../../../models/donationsNew';
import { Colors } from '../../../../../assets/theme/Colors';
import { hasAnyRoleInCurrentlySelectedSite } from '../../../../../services/auth';
import { Roles } from '../../../../../models/roles';
import { getRescuePickupLocationFullName, isHTML } from '../../../../../helpers/RescuesHelper';
import { SelectorMobileDialog } from '../../../../../components/Common/SelectorMobileDialog';

const ExpandedPanel = ({ rescue, rescuersList, disabled, isNotSCOrGreaterRoleUser }) => {
  const [isRescuerSelectorOpen, setRescuerSelectorOpen] = useState(false);
  const [pickupBeginValue, setPickupBeginValue] = useState(rescue.pickup_begin.value);
  const [pickupEndValue, setPickupEndValue] = useState(rescue.pickup_end.value);
  const [rescuerNotesValue, setRescuerNotesValue] = useState(rescue.rescuer_notes || '');
  const [pickupEndTimeValid, setPickupEndTimeValid] = useState(false);
  const { onFieldChange, isMobileView } = useContext(DonationContext);

  // update state value on redux data change
  useEffect(() => setRescuerNotesValue(rescue.rescuer_notes || ''), [rescue.rescuer_notes]);
  useEffect(() => setPickupBeginValue(rescue.pickup_begin.value), [rescue.pickup_begin.value]);
  useEffect(() => {
    setPickupEndTimeValid(false);
    setPickupEndValue(rescue.pickup_end.value);
  }, [rescue.pickup_end.value]);

  const handlePickupBeginChange = ({ event }) => {
    setPickupBeginValue(event.format('HH:mm:00'));

    if (isMobileView) {
      onFieldChange(donationManageActions.set_rescue_pickup_begin, {
        date: rescue.date.value,
        day: rescue.day,
        ordinal: rescue.ordinal,
        position: rescue.position,
        begin: event.format('HH:mm:00'),
      });
    }
  };

  const handlePickupBeginUpdate = () => {
    if (isMobileView) {
      return;
    }
    onFieldChange(donationManageActions.set_rescue_pickup_begin, {
      date: rescue.date.value,
      day: rescue.day,
      ordinal: rescue.ordinal,
      position: rescue.position,
      begin: pickupBeginValue,
    });
  };

  const handlePickupEndChange = ({ event, isValid }) => {
    if (isValid) {
      setPickupEndValue(event.format('HH:mm:00'));
    }
    setPickupEndTimeValid(isValid);

    if (isMobileView && isValid) {
      onFieldChange(donationManageActions.set_rescue_pickup_end, {
        date: rescue.date.value,
        day: rescue.day,
        ordinal: rescue.ordinal,
        position: rescue.position,
        end: event.format('HH:mm:00'),
      });
    }
  };

  const handlePickupEndUpdate = () => {
    if (isMobileView) {
      return;
    }

    if (pickupEndTimeValid) {
      onFieldChange(donationManageActions.set_rescue_pickup_end, {
        date: rescue.date.value,
        day: rescue.day,
        ordinal: rescue.ordinal,
        position: rescue.position,
        end: pickupEndValue,
      });
    }
  };

  const handleNotesChange = content => setRescuerNotesValue(content);
  const handleNotesBlur = content =>
    onFieldChange(donationManageActions.set_rescue_rescuer_notes, {
      date: rescue.date.value,
      day: rescue.day,
      ordinal: rescue.ordinal,
      position: rescue.position,
      rescuer_notes: content,
    });

  const handleRescuerUpdate = choice =>
    onFieldChange(donationManageActions.set_rescue_rescuer, {
      date: rescue.date.value,
      day: rescue.day,
      ordinal: rescue.ordinal,
      position: rescue.position,
      rescuer_id: choice ? choice.id : null,
    });

  return (
    <>
      <SelectorMobileDialog
        dialogTitle="Rescuer:"
        getOptionId={adopter => adopter.value.id}
        getOptionLabel={adopter => adopter.label}
        options={rescuersList}
        disabled={isNotSCOrGreaterRoleUser}
        open={isRescuerSelectorOpen}
        onClose={value => {
          if (value) {
            handleRescuerUpdate(value);
          }
          setRescuerSelectorOpen(false);
        }}
      />
      <Box
        sx={{
          display: 'grid',
          columnGap: '16px',
          rowGap: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <MinMaxTimePicker
            fullWidth
            disabled={disabled}
            inputVariant="outlined"
            size="small"
            label="Begin"
            value={moment(pickupBeginValue, 'HH:mm:ss')}
            maxTime={moment(pickupEndValue, 'HH:mm:ss')}
            onTimeChange={handlePickupBeginChange}
            onClose={handlePickupBeginUpdate}
          />
          <Box mx={2}>-</Box>
          <MinMaxTimePicker
            fullWidth
            disabled={disabled}
            showFieldErrors
            inputVariant="outlined"
            size="small"
            label="End"
            value={moment(pickupEndValue, 'HH:mm:ss')}
            minTime={moment(pickupBeginValue, 'HH:mm:ss')}
            onTimeChange={handlePickupEndChange}
            onClose={handlePickupEndUpdate}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" gutterBottom color="textSecondary">
            Rescuer notes
          </Typography>
          <NotesTextEditor
            value={rescuerNotesValue}
            onTextChange={handleNotesChange}
            onBlur={handleNotesBlur}
            styles={{
              '& .ql-container': {
                minHeight: 'auto',
              },
              '& .ql-editor': {
                minHeight: '60px',
                fontSize: '14px',
              },
            }}
            className={disabled ? `pointer-events-none` : ''}
          />
        </Box>

        <RescuerSelector
          isDisabled={isNotSCOrGreaterRoleUser}
          rescuersList={rescuersList}
          onChange={handleRescuerUpdate}
          onMenuOpen={() => {
            if (isMobileView) {
              setRescuerSelectorOpen(true);
            }
          }}
          rescue={rescue}
        />
      </Box>
    </>
  );
};

const AgendaRow = ({ event, rescuersList, positions, disabled }) => {
  const activeUser = useActiveUser();
  const { extendedProps: eventData } = event;
  const isDeleted = eventData.meta.schedule_status === SCHEDULE_STATUS_DELETED;
  const isOneTimeOnlyFrequency = eventData.meta.frequency === FREQUENCY_ONCE;
  const isExpandable = !isDeleted && !isOneTimeOnlyFrequency;
  const [isOpen, setIsOpen] = useState(false);
  const isNotSCOrGreaterRoleUser = !hasAnyRoleInCurrentlySelectedSite(activeUser, [
    Roles.NationalSiteDirector,
    Roles.Admin,
    Roles.SiteDirector,
    Roles.SiteCoordinator,
  ]);

  const pickupLocationFullName = getRescuePickupLocationFullName({
    pickup_location_name: eventData.meta.food_donor.pickup_location
      ? eventData.meta.food_donor.pickup_location.name
      : null,
    location: eventData.meta.food_donor ? eventData.meta.food_donor.name : 'N/A',
  });

  return (
    <Box data-eventid={event.id}>
      <Box
        sx={[
          { display: 'grid', gridTemplateColumns: '60px 25px 0.1fr 1fr 20px', columnGap: '8px', alignItems: 'center' },
          isExpandable && {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          },
        ]}
        onClick={() => setIsOpen(!isOpen)}
        padding={1}
      >
        <Box display="flex" flexDirection="column">
          <Box>{formatTime(eventData.meta.pickup_begin.value)}</Box>
          <Box>{formatTime(eventData.meta.pickup_end.value)}</Box>
        </Box>
        <Box mr={2}>
          <Box sx={{ position: 'relative', width: 25, height: 25 }}>
            <CalendarBox
              isCancelled={eventData.isCancelled}
              noReceiver={eventData.noReceiver}
              claimColor={eventData.claimColor}
              statusColor={eventData.statusColor}
            />
          </Box>
        </Box>
        <Box textAlign="center">
          {eventData.meta.position}/{positions}
        </Box>
        <Box
          sx={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography noWrap>
            <strong>From:</strong> <span>{pickupLocationFullName}</span>
          </Typography>
          <Typography noWrap>
            <strong>To:</strong> <span>{eventData.meta.receiver ? eventData.meta.receiver.name : 'N/A'}</span>
          </Typography>
          {event.title && (
            <Typography noWrap>
              <strong>Rescuer notes:</strong>{' '}
              {isHTML(event.title) ? (
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(event.title) }} />
              ) : (
                <Linkify
                  componentDecorator={(decoratedHref, decoratedText, key) => (
                    <a target="blank" href={decoratedHref} key={key}>
                      {decoratedText}
                    </a>
                  )}
                >
                  <span style={{ whiteSpace: 'pre-wrap' }}>{event.title}</span>
                </Linkify>
              )}
            </Typography>
          )}
        </Box>

        {isExpandable && <Box>{isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Box>}

        {isDeleted && (
          <Typography
            variant="caption"
            style={{
              color: Colors.donation.pickups.deleted.color,
            }}
          >
            <strong>DELETED</strong>
          </Typography>
        )}
      </Box>

      {isExpandable && (
        <Collapse in={isOpen}>
          <Box p={2}>
            <ExpandedPanel
              isNotSCOrGreaterRoleUser={isNotSCOrGreaterRoleUser}
              rescue={eventData.meta}
              rescuersList={rescuersList}
              disabled={disabled}
            />
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const AGENDA_HEIGHT = 260;

const TabledAgenda = ({ events, isLoading }) => {
  const activeSite = useActiveSite();
  const rescuersEntities = useSelector((state) => state.entities.sites.rescuers.bySiteId[activeSite.id]);
  const rescuersList = getRescuersSelectOptions(rescuersEntities || []);

  return (
    <Box position="relative">
      <OverlayLoader isLoading={isLoading} wrapperStyles={{ width: '100%' }}>
        <Box
          sx={{
            padding: 1,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--fc-border-color, #ddd)',
          }}
        >
          {Array.isArray(events) &&
            (events.length > 0 && (
            <Box
              sx={(theme) => ({
                margin: theme.spacing(-1, -1, 1, -1),
                padding: 1,
                background: '#ccc',
                color: '#000',
              })}
            >
              <Typography variant="body1">
                <strong>{moment(events[0].extendedProps.meta.date.value).format('dddd, MM/DD/YYYY')}</strong>
              </Typography>
            </Box>
          ))}
          <Box
            sx={{
              overflow: 'scroll',
              height: AGENDA_HEIGHT,
              '&&::-webkit-scrollbar': {
                WebkitAppearance: 'none',
                width: '0.65rem',
                height: '0.65rem',
              },
              '&&::-webkit-scrollbar-thumb': {
                borderRadius: 4,
                backgroundColor: 'rgba(0, 0, 0, .5)',
                WebkitBoxShadow: '0 0 1px rgba(255, 255, 255, .5)',
              },
            }}
          >
            {!Array.isArray(events) ||
              (events.length === 0 && (
                <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="subtitle2">NO EVENTS FOR SELECTED DAY</Typography>
                </Box>
              ))}

            {events.map((event, index, { length }) => (
              <Fragment key={event.id}>
                <AgendaRow event={event} rescuersList={rescuersList} positions={length} disabled={isLoading} />

                {index !== length - 1 && <Divider component="hr" />}
              </Fragment>
            ))}
          </Box>
        </Box>
      </OverlayLoader>
    </Box>
  );
};

export default TabledAgenda;
